// src/lib/utils.js
import React from "react";

// ---- helpers ----
export const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export function normalizeUrl(u) {
  const s = String(u || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  return `https://${s}`;
}

export function looksLikeUrl(u) {
  return /^https?:\/\/.+/i.test(String(u || "").trim());
}

export function canonicalKey(u) {
  let s = String(u || "").trim();
  if (!s) return "";
  if (!/^https?:\/\//i.test(s)) s = "https://" + s;
  try {
    const url = new URL(s);
    let host = url.hostname.toLowerCase();
    if (host.startsWith("www.")) host = host.slice(4);
    let path = url.pathname || "/";
    path = path.replace(/\/{2,}/g, "/");
    if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
    const params = new URLSearchParams(url.search);
    const toDelete = [];
    params.forEach((_, k) => {
      const lk = k.toLowerCase();
      if (
        lk.startsWith("utm_") ||
        lk === "fbclid" ||
        lk === "gclid" ||
        lk === "igshid" ||
        lk === "mc_cid" ||
        lk === "mc_eid" ||
        lk === "si" ||
        lk === "ref" ||
        lk === "ref_src" ||
        lk === "ref_url" ||
        lk === "campaign" ||
        lk === "trk" ||
        lk === "source"
      ) {
        toDelete.push(k);
      }
    });
    toDelete.forEach((k) => params.delete(k));
    const sorted = new URLSearchParams();
    [...params.keys()].sort().forEach((k) => sorted.append(k, params.get(k)));
    const query = sorted.toString();
    return `${host}${path}${query ? `?${query}` : ""}`;
  } catch {
    s = s.replace(/^https?:\/\//i, "").toLowerCase();
    if (s.startsWith("www.")) s = s.slice(4);
    s = s.replace(/\/{2,}/g, "/");
    if (s.endsWith("/")) s = s.slice(0, -1);
    return s;
  }
}

export function parseUrlsWithReport(raw) {
  // Split on whitespace only (newlines, tabs, spaces) - NOT commas
  // Commas can be part of valid URLs
  const tokens = String(raw || "")
    .split(/\s+/g)
    .map((s) => s.trim())
    .filter(Boolean);

  const unique = [];
  const dupesInPaste = [];
  const invalid = [];
  const seen = new Set();

  for (const tok of tokens) {
    // Skip if token is too short or doesn't look like a URL
    if (tok.length < 5 || (!tok.includes('.') && !tok.startsWith('http'))) {
      invalid.push(tok);
      continue;
    }
    
    const normalized = /^https?:\/\//i.test(tok) ? tok : `https://${tok}`;
    try {
      const u = new URL(normalized);
      // Validate that the URL has a proper hostname with at least one dot
      if (!u.hostname || u.hostname === 'localhost' || !u.hostname.includes('.')) {
        invalid.push(tok);
        continue;
      }
      const key = canonicalKey(u.toString());
      if (seen.has(key)) {
        dupesInPaste.push(u.toString());
      } else {
        seen.add(key);
        unique.push(u.toString());
      }
    } catch {
      invalid.push(tok);
    }
  }
  return { unique, dupesInPaste, invalid, candidates: tokens.length };
}

export function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

export async function copyText(t) {
  try {
    await navigator.clipboard.writeText(t);
    return true;
  } catch {
    return false;
  }
}

export function linkifyText(s) {
  const re =
    /((https?:\/\/[^\s<>"')\]]+)|([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}))/g;
  const out = [];
  let last = 0;
  s.replace(re, (match, urlMatch, _i, emailMatch, idx) => {
    if (idx > last) out.push(s.slice(last, idx));
    let text = match;
    let trailing = "";
    while (/[.,);:\]]$/.test(text)) {
      trailing = text.slice(-1) + trailing;
      text = text.slice(0, -1);
    }
    const href = emailMatch && !urlMatch ? `mailto:${text}` : text;
    out.push(
      React.createElement('a', {
        key: idx,
        href: href,
        target: "_blank",
        rel: "noreferrer noopener",
        className: "underline break-all"
      }, text)
    );
    if (trailing) out.push(trailing);
    last = idx + match.length;
    return match;
  });
  if (last < s.length) out.push(s.slice(last));
  return out;
}

function extractTextFromJson(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map((item) => extractTextFromJson(item)).filter(Boolean).join("\n");
  }
  if (typeof value === "object") {
    const simpleFields = ["how_to_request", "notes", "text", "body", "description", "content"];
    const extracted = simpleFields
      .map((key) => value[key])
      .filter((field) => typeof field === "string" && field.trim())
      .join("\n\n");
    if (extracted) return extracted;
    return Object.entries(value)
      .map(([key, nestedValue]) => {
        const nestedText = extractTextFromJson(nestedValue);
        return nestedText ? `${key}: ${nestedText}` : "";
      })
      .filter(Boolean)
      .join("\n");
  }
  return String(value);
}

export function renderValue(v) {
  if (v === undefined || v === null) return null;
  let value = v;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if ((trimmed.startsWith("{") && trimmed.endsWith("}")) || (trimmed.startsWith("[") && trimmed.endsWith("]"))) {
      try {
        value = JSON.parse(trimmed);
      } catch {
        value = trimmed;
      }
    }
  }
  const text = extractTextFromJson(value).trim();
  if (!text) return null;
  return React.createElement('span', { className: "break-words whitespace-pre-wrap" }, linkifyText(text));
}

export function toCsv(rows) {
  const cols = [
    "id","created_at","status","fee",
    "article_url","agency","location","incident_date",
    "email_for_request","how_to_request","draft_subject",
    "name_suspect","notes","footage_link","date_requested","last_reply_date"
  ];
  const esc = (x) => {
    const s = String(x ?? "");
    if (s.includes(",") || s.includes('"') || s.includes("\n")) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const header = cols.join(",");
  const lines = rows.map(r => cols.map(c => esc(r[c])).join(","));
  return [header, ...lines].join("\n");
}

export function daysSince(dateStr) {
  if (!dateStr) return null;
  const then = new Date(dateStr);
  const now = new Date();
  const diff = now - then;
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}
